import { useCallback, useMemo } from 'react';
import mapValues from 'lodash/mapValues';
import isEmpty from 'lodash/isEmpty';
import Base64 from '../utils/base64';
import { log } from '../utils/log';
import { usePaginationAndSortParams } from '../hooks/usePaginationAndSortParams';
import { FilterSettingLegacy, ProductFilter } from '../../store/api';
import { useSearchParams } from 'react-router-dom';

const FILTER_QUERY_PARAM = 'filter';

/**
 * Manages all filters and provides ways to set or unset them.
 */
export const useProductFilters = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { resetPageParam } = usePaginationAndSortParams();
    const filterParam = searchParams.get(FILTER_QUERY_PARAM) ?? '';
    const productFilter = useMemo(() => deserializeFilter(filterParam), [filterParam]);

    /**
     * Manages the selected filters via the query parameter 'filter'.
     */
    const setFilters = useCallback(
        (filter: ProductFilter) => {
            const newValue = serializeFilter(filter);

            setSearchParams(
                current => {
                    if (newValue) {
                        current.set(FILTER_QUERY_PARAM, newValue);
                    } else {
                        current.delete(FILTER_QUERY_PARAM);
                    }
                    return resetPageParam(current);
                },
                { replace: true }
            );
        },
        [resetPageParam, setSearchParams]
    );

    const resetAllFilters = useCallback(() => {
        setFilters({});
    }, [setFilters]);

    const changeFilter = useCallback(
        (category: string, include: ReadonlyArray<string>, exclude: ReadonlyArray<string>) => {
            if (isEmpty(include) && isEmpty(exclude)) {
                const newFilters = { ...productFilter };
                delete newFilters[category];
                setFilters(newFilters);
            } else {
                setFilters({
                    ...productFilter,
                    [category]: {
                        include: [...include],
                        exclude: [...exclude]
                    }
                });
            }
        },
        [productFilter, setFilters]
    );

    return {
        productFilter,
        changeFilter,
        resetAllFilters
    };
};

export function updateFilterParam(params: URLSearchParams, nextProductFilter: ProductFilter) {
    const newValue = serializeFilter(nextProductFilter);

    if (newValue) {
        params.set(FILTER_QUERY_PARAM, newValue);
    } else {
        params.delete(FILTER_QUERY_PARAM);
    }

    return params;
}

/**
 * Serializes a filter to a query parameter. Performs a mapping from 'includeFilterValues' to 'include' and
 * 'excludeFilterValues' to 'exclude' to be consistent with the way filters are stored as a query parameter.
 */
function serializeFilter(filter: ProductFilter): string {
    if (isEmptyProductFilters(filter)) {
        return '';
    }

    return Base64.encode(JSON.stringify(filter));
}

/**
 * Deserializes a filter query parameter to a ProductFilter. Performs a mapping from 'include' to 'includeFilterValues'
 * and 'exclude' to 'excludeFilterValues' to be consistent with the way filters are stored as a query parameter.
 */
function deserializeFilter(filter: string | undefined): ProductFilter {
    if (!filter) {
        return {};
    }

    try {
        const parsed = JSON.parse(Base64.decode(filter)) as Record<string, FilterSettingLegacy>;

        return mapValues(parsed, (filterSetting: FilterSettingLegacy) => ({
            include: filterSetting.includeFilterValues ?? filterSetting.include ?? [],
            exclude: filterSetting.excludeFilterValues ?? filterSetting.exclude ?? []
        }));
    } catch (e) {
        log.info('Cannot parse query filters', { filter });
        return {};
    }
}

export function isEmptyProductFilters(filters: ProductFilter): boolean {
    return isEmpty(filters);
}
